import ReactDOM from 'react-dom/client'
import { pipe } from 'remeda'
import { lazyImport } from '@helpers'
import { withErrorBoundary, withI18next, withSuspense, withTheme } from '@hocs'
import type { Breadcrumb, MenuItem } from '@velasca/types'
import { parseJSONSafe } from '@velasca/utils/parse-json-safe'
import { traverseMenu } from '@velasca/utils/traverse-menu'

const { Footer } = lazyImport(() => import('@velasca/ui/footer'), 'Footer')

interface BreadcrumbsData {
  closestHandle: string
  pageType:
    | 'page'
    | 'macropage'
    | 'collection'
    | 'macrocollection'
    | 'product'
    | 'homepage'
    | 'unknown'
  lastBreadcrumb: Breadcrumb
}

const footerEl = document.getElementById('footer-react')
const footerDataEl = document.getElementById('footer-data')
const footerData = footerDataEl ? parseJSONSafe(footerDataEl.innerText) : {}

const breadcrumbsDataEl = document.getElementById('breadcrumbs-json')
const breadcrumbsData: BreadcrumbsData = breadcrumbsDataEl
  ? parseJSONSafe(breadcrumbsDataEl.innerText)
  : {}

/**
 * Determines the breadcrumbs to be displayed based on menu and page type.
 *
 * @param menu - The menu tree.
 * @returns The breadcrumbs array or null if not found.
 */
const getBreadcrumbs = (menu: MenuItem[]): Breadcrumb[] | null => {
  const { closestHandle, pageType, lastBreadcrumb } = breadcrumbsData

  switch (pageType) {
    case 'homepage': {
      // return an empty array on homepage to prevent double Home link
      return []
    }
    case 'page':
    case 'collection': {
      const foundBranch = traverseMenu(menu, closestHandle)

      return foundBranch ?? [lastBreadcrumb]
    }
    case 'macropage':
    case 'macrocollection':
    case 'product': {
      const foundBranch = traverseMenu(menu, closestHandle)

      if (!foundBranch) {
        return [lastBreadcrumb]
      }

      const lastFoundBreadcrumb = foundBranch?.[foundBranch.length - 1]
      const isLastBreadcrumbDuplicate =
        lastFoundBreadcrumb?.text === lastBreadcrumb.text

      return isLastBreadcrumbDuplicate
        ? foundBranch
        : [...foundBranch, lastBreadcrumb]
    }
    case 'unknown': {
      return [lastBreadcrumb]
    }
  }
}

interface RenderFooterAppParams {
  menu: MenuItem[]
}

/**
 * Renders the footer application.
 *
 * @param params - Contains the menu tree.
 */
export const renderFooterApp = ({ menu }: RenderFooterAppParams) => {
  if (!footerEl || !footerData) {
    return
  }

  const breadcrumbs = getBreadcrumbs(menu)

  const FooterApp = () => (
    <Footer
      {...footerData}
      breadcrumbs={
        breadcrumbs
          ? [{ text: 'Home', link: '/' }, ...breadcrumbs]
          : footerData.breadcrumbs
      }
    />
  )

  const FooterAppWrapper = pipe(
    FooterApp,
    withSuspense,
    withI18next,
    withErrorBoundary,
    withTheme,
  )

  ReactDOM.createRoot(footerEl).render(<FooterAppWrapper />)
}
